import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PATHS } from '../../../../constants/routes';
import Header from '../../../shared/Header/Header';
import Footer from '../../../shared/Footer/Footer';
import SectionHeader from '../../../layouts/showcaseLayouts/Section/SectionHeader/SectionHeader';
import SectionBody from '../../../layouts/showcaseLayouts/Section/SectionBody/SectionBody';
import './luxuryArtPage.scss';

const LuxuryArtPage: React.FC = () => {
    useEffect(() => {
        const items = Array.from(document.querySelectorAll<HTMLElement>('.luxuryArtPage [data-animate]'));
        if (!('IntersectionObserver' in window)) {
            items.forEach((el) => el.classList.add('is-visible'));
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        (entry.target as HTMLElement).classList.add('is-visible');
                        observer.unobserve(entry.target);
                    }
                });
            },
            { root: null, threshold: 0.15, rootMargin: '0px 0px -10% 0px' },
        );
        items.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    return (
        <div className="luxuryArtPage">
            <Header />
            <div className="luxuryArtPage_headerWrapper">
                <SectionHeader title="Коллекция золотой шелк" />
            </div>
            <SectionBody>
                <>
                    <div className="luxuryArtPage__hero" data-animate="fade-in">
                        <figure className="luxuryArtPage__heroFigure">
                            <img className="luxuryArtPage__mainImg" src="/luxury-art/01.jpg" alt="Иллюстрация 1" loading="eager" decoding="async" />
                            <figcaption className="visually-hidden">Коллекция «Золотой шелк»</figcaption>
                        </figure>
                    </div>
                    <article className="luxuryArtPage__textCard" data-animate="fade-up" aria-label="Описание коллекции Золотой шелк">
                        <p>Коллекция "Золотой шелк" от торгового дома С.Габай - это путешествие в мир королевской роскоши, безмятежности и неги.</p>
                        <p>Одеяла Голд из редчайшего золотого шелка созданы для ценителей комфортной жизни и эксклюзивного стиля.</p>
                        <p>
                            Золотой шелк Голд издавна считался привилегией членов императорской семьи. Шелкопряды, способные вырабатывать уникальную золотую шелковую
                            нить, в древности были очень редки. Существует легенда, что китайский император Хуан Ди даже выпустил приказ, запрещающий носить изделия
                            из золотого шелка всем, кроме членов императорской семьи.
                        </p>
                    </article>
                    <section className="luxuryArtPage__duo" aria-label="История и свойства золотого шелка">
                        <div className="luxuryArtPage__duoRow luxuryArtPage__duoRow--left" data-animate="slide-left">
                            <figure className="luxuryArtPage__duoFigure">
                                <img className="luxuryArtPage__img" src="/luxury-art/02.jpg" alt="Иллюстрация 2" loading="lazy" decoding="async" />
                            </figure>
                            <div className="luxuryArtPage__duoText">
                                <p>Именно золотой шелковой нитью, по праву считающейся самой прочной и стерильной, врачи зашивали раны.</p>
                                <p>
                                    В мире существует несколько видов шелка, различные по длине и толщине нити, структуре, прочности и натяжению. По всем этим
                                    характеристикам натуральный золотой шелк является безусловным лидером. Он имеет максимально однородную структуру, самую длинную
                                    нить и наивысшую прочность, поэтому считается редким и премиальным материалом в мире текстиля.
                                </p>
                                <p>Одеяло из такого материала дарит нежнейшее и незабываемое прикосновение, создавая поистине королевскую атмосферу в вашем доме.</p>
                                <p>
                                    Своим дивным цветом шелк Голд обязан природным пигментам, таящимся в листьях шелковицы, которыми питаются шелкопряды. Под
                                    воздействием определенного гена Y шелкопряды извлекают из этих листьев солнечные каротиноиды, флавоноиды и лютеин, превращая их в
                                    уникальный золотой шелк.
                                </p>
                            </div>
                        </div>
                    </section>
                    <section className="luxuryArtPage__duo" aria-label="Природные пигменты и преимущества">
                        <div className="luxuryArtPage__duoRow luxuryArtPage__duoRow--right" data-animate="slide-right">
                            <div className="luxuryArtPage__duoText">
                                <p>
                                    Именно благодаря этим микроэлементам изделия из золотого шелка приобретают полезные свойства для кожи и волос, омолаживая их. А
                                    фиброин, один из протеинов золотого шелка, замедляет процессы старения и способствует оздоровлению организма, предотвращению
                                    простудных заболеваний, артрита и ревматизма.
                                </p>
                                <p>
                                    В состав наполнителя одеяла Голд входят исключительно натуральные волокна золотого шелка, способные поддерживать комфортную
                                    температуру тела во время сна в любое время года. Эстетичный чехол, выполненный из хлопка и вискозы, нежный на ощупь, не
                                    накапливает статическое электричество и хорошо пропускает воздух.
                                </p>
                                <p>
                                    В ассортименте торгового дома С.Габай представлены одеяла Голд из премиального золотого шелка в 4-х сезонных модификациях:
                                    летнее, весеннее, зимнее и всесезонное. Каждое изделие бережно изготовлено вручную при строгом соблюдении стандартов качества.
                                </p>
                            </div>
                            <figure className="luxuryArtPage__duoFigure">
                                <img className="luxuryArtPage__img" src="/luxury-art/03.jpg" alt="Иллюстрация 3" loading="lazy" decoding="async" />
                            </figure>
                        </div>
                    </section>
                    <div className="luxuryArtPage__cta" data-animate="fade-up">
                        <Link className="luxuryArtPage__link luxuryArtPage__link--xl" to={`${PATHS.showcase}?typesOfFiller=gold`}>
                            Купить
                            <img className="luxuryArtPage__link_img" src="/icons/arrow-up-right.svg" alt="" />
                        </Link>
                    </div>
                </>
            </SectionBody>
            <Footer />
        </div>
    );
};

export default LuxuryArtPage;
